import {
  formatMoney,
  getAgentById,
  getCheckoutSessionById,
  getLoanById,
  getPayoutSummaryById,
} from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ICON_NAMES } from '@/config/config';
import { Layout, NavSection } from '@/config/types';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';
import { useCache } from '@/hooks/requests/useCache';
import { getPathIdFromUrl } from '@/utils/getPathIdFromUrl';

export function useBreadCrumbs(layout?: Layout) {
  interface BreadCrumb {
    linkTo: string;
    pathId: string;
    titleTextId?: string;
    name?: string;
    iconName?: string;
  }

  const { merchantId, agentId, channelId, loanId, payoutId, checkoutSessionId } = useParams();

  const agent = agentId ? useCache(getAgentById, { agentId })?.data : undefined;
  const loan = loanId ? useCache(getLoanById, { loanId })?.data : undefined;
  const merchantStructure = merchantId ? useGetMerchantStructureById(merchantId).merchantStructure : undefined;
  const payoutSummary = payoutId ? useCache(getPayoutSummaryById, { id: payoutId }).data : undefined;
  const checkoutSession = checkoutSessionId ? useCache(getCheckoutSessionById, { checkoutSessionId }).data : undefined;

  const { pathname } = useLocation();
  const { getTextIdTranslation } = useTranslation();

  useEffect(() => {
    if (layout && pathname) updateBreadCrumbs();
  }, [pathname]);

  useEffect(() => {
    updateBreadCrumbs();
  }, [agent, merchantStructure, loan, payoutSummary, checkoutSession]);

  const [state, setState] = useState({
    navSection: undefined as NavSection | undefined,
    breadCrumbs: [] as BreadCrumb[],
  });
  const { breadCrumbs, navSection } = state;

  const updateBreadCrumbs = (): void => {
    const pathNodes = pathname.split('/').filter((e) => e);
    const newBreadCrumbs = [] as BreadCrumb[];
    pathNodes.forEach((_, key) => {
      const pathParts = pathNodes.slice(0, key + 1);
      const supposedPath = `/${pathParts.join('/')}/`;
      const breadCrumb = getBreadCrumbFromPath(supposedPath);
      if (breadCrumb) {
        newBreadCrumbs.push(breadCrumb);
      }
    });
    const firstBreadCrumb = newBreadCrumbs[0]?.pathId;
    const navSection = getNavSectionFromLinkPath(firstBreadCrumb);
    if (!firstBreadCrumb) {
      const newBreadCrumb = getBreadCrumbFromPath('/');
      if (newBreadCrumb) newBreadCrumbs.push(newBreadCrumb);
    }
    setState({
      navSection,
      breadCrumbs: newBreadCrumbs,
    });
  };

  const getNavSectionFromLinkPath = (path: string): NavSection | undefined => {
    if (!layout) return;
    for (const navSection of layout.navSections) {
      const hasTargetLink = navSection.links.find((link) => link.path === path);
      if (hasTargetLink) return navSection;
    }
  };

  const getNamedBreadCrumbProps = (pathId?: string): { name?: string; iconName?: string } => {
    const noInfo = { name: '', iconName: undefined };
    if (!pathId) return noInfo;
    const currentChannelByChannelId = merchantStructure?.merchant.channels.find((channel) => channel.id === channelId);
    switch (pathId) {
      case DYNAMIC_PAGE_PATHS.MERCHANT:
        return {
          name: merchantStructure?.merchant.businessName,
          iconName: ICON_NAMES.MERCHANT,
        };
      case DYNAMIC_PAGE_PATHS.AGENT:
        return {
          name: agent?.name,
          iconName: ICON_NAMES.AGENT,
        };
      case DYNAMIC_PAGE_PATHS.CHANNEL:
        if (currentChannelByChannelId)
          return {
            name: currentChannelByChannelId?.description,
            iconName: ICON_NAMES.CHANNEL,
          };
        break;
      case DYNAMIC_PAGE_PATHS.PAYOUT:
        return {
          name: payoutSummary ? `${payoutSummary?.reference}` : undefined,
        };
      case DYNAMIC_PAGE_PATHS.CHECKOUT_SESSION_DETAIL:
        return {
          name: checkoutSession?.customer?.fullName
            ? `${checkoutSession.customer.fullName.firstName} ${checkoutSession.customer.fullName.lastName}`
            : undefined,
        };
      case DYNAMIC_PAGE_PATHS.CONSUMER_LOAN:
      case DYNAMIC_PAGE_PATHS.AUTO_LOAN:
      case DYNAMIC_PAGE_PATHS.CREDIT_LINE:
      case DYNAMIC_PAGE_PATHS.ORDER:
      case DYNAMIC_PAGE_PATHS.PAY_BY_LINK:
        if (loan) {
          return {
            name: `${loan.onboardingDetails[0].invitation.fullName.firstName} ${
              loan.onboardingDetails[0].invitation.fullName.lastName
            } ${getTextIdTranslation('BY')} ${formatMoney({
              value: loan.loanConditions.principal.amount,
              currency: loan.loanConditions.currency,
            })}`,
          };
        }
    }
    return noInfo;
  };

  const getBreadCrumbFromPath = (supposedPath: string): BreadCrumb | undefined => {
    if (!layout?.pagesByPath) return undefined;
    const pathId = getPathIdFromUrl(supposedPath, layout);
    if (!pathId) return;
    const { name, iconName } = getNamedBreadCrumbProps(pathId);
    return {
      linkTo: supposedPath,
      pathId,
      titleTextId: layout?.pagesByPath[pathId].titleTextId || '',
      name,
      iconName,
    };
  };

  return { breadCrumbs, navSection };
}
